import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DisplayPredictions from "./components/DisplayPredictions";
import StructuredJSONDisplay from "./components/StructuredJSONDisplay";
import ObjectsDisplay from "./components/ObjectsDisplay";
import VirtualizedPages from "./components/VirtualizedPages";
import DocumentUpload from "../../modals/DocumentUpload";
import AddMoleculeModal from "../../modals/AddMoleculeModal";
import AddReactionModal from "../../modals/AddReactionModal";
import { Tooltip } from "./components/Tooltip";
import CommentLayer from "./components/CommentLayer";
import CommentSidebar, { PendingPin } from "./components/CommentSidebar";
import type { Comment } from "./components/CommentThread";
import { useZoom } from "../../hooks/useZoom";
import { usePan } from "../../hooks/usePan";
import { useAuth } from "../../utils/AuthContext";
import {
  transformToLegacyFormat,
  transformToPositionedFormat,
  transformToDigitalFormat,
} from "../../utils/transformData";
import { buildRawJsonObject } from "../../utils/documentJson";
import {
  getDocument,
  deletePage,
  getDocumentPages,
  deleteDocument,
  renameDocument,
  getPageData,
  getDocumentComments,
  getAllPagesData,
  restructurePage,
  adminUpdateDocumentStatus,
  addMoleculeToPage,
  mergeMoleculesIntoPage,
  updateMoleculeSmiles,
  deleteMolecule,
  addReactionToPage,
  updateReactionSmiles,
  deleteReaction,
} from "../../utils/Api";
import {
  getCachedPageDataWithBbox,
  getCachedPageImage,
  invalidatePageCache,
} from "../../utils/pageCache";

import {
  FilePlus,
  Pencil,
  Trash,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  House,
  ZoomIn,
  ZoomOut,
  SlidersHorizontal,
  MessageSquarePlus,
  RefreshCw,
  Loader2,
  FlaskConical,
  Upload,
} from "lucide-react";

import UserNavbar from "../../components/UserNavbar";
import DocumentViewSkeleton from "../../components/SkeletonLoading/DocumentViewSkeleton";
import DeleteDocumentModal from "../../modals/DeleteDocumentModal";
import DeletePageModal from "../../modals/DeletePageModal";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import { useUpload } from "../../contexts/UploadContext";

interface DocumentData {
  name: string;
  [key: string]: any;
}

interface PageData {
  id: string;
  status: string;
}

type PagesData = PageData[];

// Fixed slot height per page used by the virtualizer.
// 800px image + ~32px header + ~32px bottom spacing.
const SLOT_HEIGHT = 864;

// Pages fetched per /pages-data request. Large enough to keep the round-trip
// count low; small enough that a single response stays well under Cloud Run's
// memory/timeout limits even for documents with hundreds of pages.
const PAGE_BATCH_SIZE = 50;
// Raw data is much heavier (per-page bbox, graphs, etc.), so use a smaller
// batch when include_raw=true to keep response size manageable.
const PAGE_BATCH_SIZE_RAW = 25;
// How many batches we keep in flight at once. Browsers cap concurrent
// connections per host (~6), so 4 leaves headroom for other requests
// (images, comments, etc.) while making large-doc loads feel much faster.
const PAGE_FETCH_CONCURRENCY = 4;

function renderJsonWithHighlights(
  text: string,
  query: string,
  currentIdx: number,
): React.ReactNode {
  if (!query) return text;
  const lower = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const nodes: React.ReactNode[] = [];
  let pos = 0;
  let matchIdx = 0;
  while (pos <= text.length) {
    const found = lower.indexOf(lowerQuery, pos);
    if (found === -1) {
      nodes.push(text.slice(pos));
      break;
    }
    if (found > pos) nodes.push(text.slice(pos, found));
    nodes.push(
      <mark
        key={`${found}-${matchIdx}`}
        {...(matchIdx === currentIdx && { "data-json-current-match": "true" })}
        style={{
          background: matchIdx === currentIdx ? "#f97316" : "#fbbf24",
          color: "#111",
          borderRadius: "2px",
        }}
      >
        {text.slice(found, found + lowerQuery.length)}
      </mark>,
    );
    pos = found + lowerQuery.length;
    matchIdx++;
  }
  return nodes;
}

// ─── Memoised left-panel page ────────────────────────────────────────────────
const LeftPageItem = React.memo<{
  documentID: string;
  pageId: string;
  index: number;
  zoom: number;
  panCursor: string;
  panHandlers: { onPointerDown: (e: React.PointerEvent<HTMLElement>) => void };
  selectedTerm: string;
  onHoverObject: (uuid: string | null) => void;
  hoveredObjectUuid: string | null;
  highlightedBbox?: number[] | null;
  onDeletePage: (index: number) => void;
}>(
  ({
    documentID,
    pageId,
    index,
    zoom,
    panCursor,
    panHandlers,
    selectedTerm,
    onHoverObject,
    hoveredObjectUuid,
    highlightedBbox,
    onDeletePage,
  }) => (
    <div className="px-2 md:px-5 relative">
      <div className="mb-2 text-sm font-semibold text-slate-600 text-center flex items-center justify-center gap-2">
        <span>Page {index + 1}</span>
        <button
          onClick={() => onDeletePage(index)}
          className="p-1 hover:bg-red-100 rounded transition-colors"
          title="Delete this page"
        >
          <Trash size={14} className="text-red-600" />
        </button>
      </div>
      <div
        className="h-[60vh] md:h-[800px] overflow-auto"
        style={{ cursor: panCursor }}
        {...panHandlers}
      >
        <div
          style={{
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            minWidth: "100%",
            minHeight: "100%",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${(1 / zoom) * 100}%`,
              height: `${(1 / zoom) * 100}%`,
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
            }}
          >
            <DisplayPredictions
              documentID={documentID}
              pageID={pageId}
              mode="image-only"
              selectedTerm={selectedTerm}
              onHoverObject={onHoverObject}
              highlightedObjectUuid={hoveredObjectUuid}
              {...(highlightedBbox != null && { highlightedBbox })}
            />
          </div>
        </div>
      </div>
    </div>
  ),
);
LeftPageItem.displayName = "LeftPageItem";

// ─── Memoised right-panel positioned page ───────────────────────────────────
const RightPageItem = React.memo<{
  documentID: string;
  pageId: string;
  index: number;
  zoom: number;
  panCursor: string;
  panHandlers: { onPointerDown: (e: React.PointerEvent<HTMLElement>) => void };
  activeSearchQuery: string;
  isCurrentSearchResult: boolean;
  currentOccurrenceIndex: number | undefined;
  selectedTerm: string;
  onTermSelect: (term: string) => void;
  hoveredParentUuid: string | null;
  pageRef: (el: HTMLDivElement | null) => void;
  structuredPageRef: (el: HTMLDivElement | null) => void;
  refreshKey?: number;
  showDrawings?: boolean;
}>(
  ({
    documentID,
    pageId,
    index,
    zoom,
    panCursor,
    panHandlers,
    activeSearchQuery,
    isCurrentSearchResult,
    currentOccurrenceIndex,
    selectedTerm,
    onTermSelect,
    hoveredParentUuid,
    pageRef,
    structuredPageRef,
    refreshKey,
    showDrawings,
  }) => (
    <div
      ref={pageRef}
      className="border-b border-gray-200 last:border-b-0 group"
    >
      <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-600 sticky top-0 z-10 border-b border-gray-200 flex items-center justify-between">
        <span>Page {index + 1}</span>
      </div>
      <div
        ref={structuredPageRef}
        className="h-[60vh] md:h-[800px] overflow-auto"
        style={{ cursor: panCursor }}
        {...panHandlers}
      >
        <div
          style={{
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            minWidth: "100%",
            minHeight: "100%",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${(1 / zoom) * 100}%`,
              height: `${(1 / zoom) * 100}%`,
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
            }}
            className="px-2 md:px-5 py-3 md:py-5"
          >
            <StructuredJSONDisplayWrapper
              documentID={documentID}
              pageId={pageId}
              {...(activeSearchQuery && { searchQuery: activeSearchQuery })}
              {...(isCurrentSearchResult &&
                typeof currentOccurrenceIndex === "number" && {
                  isCurrentSearchResult: true,
                  currentOccurrenceIndex,
                })}
              {...(selectedTerm && { selectedTerm })}
              onTermSelect={onTermSelect}
              hoveredParentUuid={hoveredParentUuid}
              {...(refreshKey !== undefined && { refreshKey })}
              {...(showDrawings !== undefined && { showDrawings })}
            />
          </div>
        </div>
      </div>
    </div>
  ),
);
RightPageItem.displayName = "RightPageItem";

const StructuredJSONDisplayWrapper: React.FC<{
  documentID: string;
  pageId: string;
  searchQuery?: string;
  isCurrentSearchResult?: boolean;
  currentOccurrenceIndex?: number;
  selectedTerm?: string;
  onTermSelect?: (term: string) => void;
  hoveredParentUuid?: string | null;
  refreshKey?: number;
  showDrawings?: boolean;
}> = ({
  documentID,
  pageId,
  searchQuery,
  isCurrentSearchResult,
  currentOccurrenceIndex,
  selectedTerm,
  onTermSelect,
  hoveredParentUuid,
  refreshKey,
  showDrawings,
}) => {
  const [rawData, setRawData] = useState<any>(null);
  const [readableData, setReadableData] = useState<any>(null);
  const [imageHeight, setImageHeight] = useState<number>(800);
  const [imageWidth, setImageWidth] = useState<number>(600);

  useEffect(() => {
    let mounted = true;
    let img: HTMLImageElement | null = null;

    const fetchData = async () => {
      try {
        const { data } = await getCachedPageDataWithBbox(documentID, pageId);
        const transformed = transformToPositionedFormat(data.readable_data);
        if (mounted) {
          setRawData(transformed);
          setReadableData(data.readable_data);
        }
      } catch (error) {
        console.error(`Error fetching data for page ${pageId}:`, error);
      }
    };

    const fetchImage = async () => {
      try {
        const { data } = await getCachedPageImage(documentID, pageId);
        img = new Image();
        img.onload = () => {
          if (mounted) {
            setImageHeight(img!.naturalHeight);
            setImageWidth(img!.naturalWidth);
          }
        };
        img.src = data.image;
      } catch (error) {
        console.error(`Error fetching image for page ${pageId}:`, error);
      }
    };

    fetchData();
    fetchImage();

    return () => {
      mounted = false;
      if (img) {
        img.onload = null;
      }
    };
  }, [documentID, pageId, refreshKey]);

  if (!rawData || !readableData) {
    return <div className="p-4 text-gray-400 text-sm">Loading page data…</div>;
  }

  // Transparent fallback if the data loaded but has no positional content
  if (!rawData.textLines?.length && !rawData.molecules?.length) {
    console.warn(
      `[StructuredJSONDisplayWrapper] page ${pageId} has rawData but no textLines/molecules. rawData:`,
      rawData,
    );
    return (
      <div className="p-4 text-gray-400 text-sm">
        No structured content available for this page.
      </div>
    );
  }

  return (
    <StructuredJSONDisplay
      rawData={rawData}
      readableData={readableData}
      imageHeight={imageHeight}
      imageWidth={imageWidth}
      {...(searchQuery !== undefined && { searchQuery })}
      {...(isCurrentSearchResult !== undefined && { isCurrentSearchResult })}
      {...(currentOccurrenceIndex !== undefined && { currentOccurrenceIndex })}
      {...(selectedTerm !== undefined && { selectedTerm })}
      {...(onTermSelect && { onTermSelect })}
      {...(hoveredParentUuid !== undefined && { hoveredParentUuid })}
      {...(showDrawings !== undefined && { showDrawings })}
    />
  );
};

/** Fetches positional data for all pages and renders ObjectsDisplay. */
const ChemistryDataLoader: React.FC<{
  documentID: string;
  pages: string[];
  canShareText?: boolean;
  onHoverObject?: (uuid: string | null) => void;
  hoveredObjectUuid?: string | null;
  onScrollToPage?: (pageIndex: number) => void;
  onHoverBbox?: (overlay: { pageIndex: number; bbox: number[] } | null) => void;
  refreshKey?: number;
  onEditMolecule?: (
    pageIndex: number,
    moleculeUuid: string,
    currentSmiles: string,
  ) => void;
  onDeleteMolecule?: (pageIndex: number, moleculeUuid: string) => void;
  onBulkDeleteMolecules?: (
    items: { pageIndex: number; moleculeUuid: string }[],
  ) => void | Promise<void>;
  onEditReaction?: (
    pageIndex: number,
    reactionUuid: string,
    currentRxnSmiles: string,
  ) => void;
  onDeleteReaction?: (pageIndex: number, reactionUuid: string) => void;
  onBulkDeleteReactions?: (
    items: { pageIndex: number; reactionUuid: string }[],
  ) => void | Promise<void>;
}> = ({
  documentID,
  pages,
  canShareText = true,
  onHoverObject,
  hoveredObjectUuid,
  onScrollToPage,
  onHoverBbox,
  refreshKey = 0,
  onEditMolecule,
  onDeleteMolecule,
  onBulkDeleteMolecules,
  onEditReaction,
  onDeleteReaction,
  onBulkDeleteReactions,
}) => {
  const [pagesData, setPagesData] = useState<
    { pageId: string; rawData: any }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    setPagesData([]);

    const totalPages = pages.length;

    const load = async () => {
      try {
        if (totalPages === 0) {
          if (mounted) setLoading(false);
          return;
        }

        const offsets: number[] = [];
        for (let off = 0; off < totalPages; off += PAGE_BATCH_SIZE) {
          offsets.push(off);
        }

        // Sparse array keyed by batch index. Undefined entries = not yet
        // fetched. After each batch completes we walk this array and push
        // the contiguous prefix (from index 0) into state, so per-page
        // indexing stays correct even though batches arrive out of order.
        const batchResults: (
          | { pageId: string; rawData: any }[]
          | undefined
        )[] = new Array(offsets.length);

        let nextBatchIdx = 0;

        const worker = async () => {
          while (mounted) {
            const idx = nextBatchIdx++;
            if (idx >= offsets.length) return;
            const offset = offsets[idx]!;
            const { data } = await getAllPagesData(documentID, {
              offset,
              limit: PAGE_BATCH_SIZE,
            });
            if (!mounted) return;
            batchResults[idx] = (
              data as { id: string; readable_data: any }[]
            ).map((p) => ({
              pageId: p.id,
              rawData: transformToPositionedFormat(p.readable_data),
            }));

            // Walk contiguous prefix of completed batches
            const contiguous: { pageId: string; rawData: any }[] = [];
            for (const batch of batchResults) {
              if (batch === undefined) break;
              contiguous.push(...batch);
            }
            if (mounted) setPagesData(contiguous);
          }
        };

        await Promise.all(
          Array.from({ length: PAGE_FETCH_CONCURRENCY }, () => worker()),
        );

        if (mounted) setLoading(false);
      } catch {
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [documentID, pages.length, retryKey, refreshKey]);

  if (loading && pagesData.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 h-48 text-sm text-gray-400">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-[#1d365d] rounded-full animate-spin" />
        Loading document objects
      </div>
    );
  }

  if (error && pagesData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3 text-sm text-gray-500">
        <span>Failed to load objects.</span>
        <button
          onClick={() => setRetryKey((k) => k + 1)}
          className="px-3 py-1 bg-[#1d365d] text-white rounded hover:bg-[#152848] transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <ObjectsDisplay
        documentId={documentID}
        pagesData={pagesData}
        canShareText={canShareText}
        {...(onHoverObject !== undefined && { onHoverObject })}
        {...(hoveredObjectUuid !== undefined && { hoveredObjectUuid })}
        {...(onScrollToPage !== undefined && { onScrollToPage })}
        {...(onHoverBbox !== undefined && { onHoverBbox })}
        {...(onEditMolecule && { onEditMolecule })}
        {...(onDeleteMolecule && { onDeleteMolecule })}
        {...(onBulkDeleteMolecules && { onBulkDeleteMolecules })}
        {...(onEditReaction && { onEditReaction })}
        {...(onDeleteReaction && { onDeleteReaction })}
        {...(onBulkDeleteReactions && { onBulkDeleteReactions })}
      />
      {loading && pagesData.length > 0 && (
        <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-400 border-t border-gray-100">
          <div className="w-3 h-3 border-2 border-gray-300 border-t-[#1d365d] rounded-full animate-spin" />
          Loading more pages…
        </div>
      )}
      {error && pagesData.length > 0 && (
        <div className="flex items-center justify-center gap-2 py-3 text-xs text-amber-600 border-t border-gray-100">
          Failed to load some pages.
          <button
            onClick={() => setRetryKey((k) => k + 1)}
            className="underline hover:text-amber-700"
          >
            Retry
          </button>
        </div>
      )}
    </>
  );
};

const View: React.FC = () => {
  const { id: documentID } = useParams<{ id: string }>();
  const { user, isAdmin } = useAuth();
  const [documentName, setDocumentName] = useState<string>("Loading...");
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [isAddPagesModalOpen, setIsAddPagesModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletePageModalOpen, setIsDeletePageModalOpen] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingPage, setIsDeletingPage] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState<string>("");
  const [, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentMode, setCommentMode] = useState(false);
  const [pendingPin, setPendingPin] = useState<PendingPin | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeSearchQuery, setActiveSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<
    Array<{ pageId: string; occurrenceIndex: number }>
  >([]);
  const [currentResultIndex, setCurrentResultIndex] = useState<number>(0);
  const [selectedTerm, setSelectedTerm] = useState<string>("");
  const [activeTab, setActiveTab] = useState<
    "positioned" | "chemistry" | "json"
  >("positioned");
  const [hoveredObjectUuid, setHoveredObjectUuid] = useState<string | null>(
    null,
  );
  const [hoveredBboxOverlay, setHoveredBboxOverlay] = useState<{
    pageIndex: number;
    bbox: number[];
  } | null>(null);
  const [bboxVisibility, setBboxVisibility] = useState({
    text: true,
    molecules: true,
    tables: false,
  });
  const [bboxLocks, setBboxLocks] = useState({
    text: false,
    molecules: false,
    tables: false,
  });
  const [showBboxMenu, setShowBboxMenu] = useState(false);
  const bboxMenuRef = useRef<HTMLDivElement>(null);
  const [allPagesJsonData, setAllPagesJsonData] = useState<any>(null);
  const [allPagesRawData, setAllPagesRawData] = useState<any[]>([]);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonError, setJsonError] = useState(false);
  const inFlightJsonRef = useRef<Promise<any> | null>(null);
  const [mobileView, setMobileView] = useState<"image" | "data">("image");
  const [canShareText, setCanShareText] = useState<boolean>(true);
  const [showDrawings, setShowDrawings] = useState<boolean>(true);
  const [pageRefreshKeys, setPageRefreshKeys] = useState<Record<string, number>>({});
  const [restructuringPageId, setRestructuringPageId] = useState<string | null>(null);
  const [mergingPageId, setMergingPageId] = useState<string | null>(null);
  const [moleculeModal, setMoleculeModal] = useState<
    | {
        mode: "add";
        pageId: string;
        pageIndex: number;
      }
    | {
        mode: "edit";
        pageId: string;
        pageIndex: number;
        moleculeUuid: string;
        initialSmiles: string;
      }
    | null
  >(null);
  const [reactionModal, setReactionModal] = useState<
    | {
        mode: "add";
        pageId: string;
        pageIndex: number;
      }
    | {
        mode: "edit";
        pageId: string;
        pageIndex: number;
        reactionUuid: string;
        initialRxnSmiles: string;
      }
    | null
  >(null);
  const [objectsRefreshKey, setObjectsRefreshKey] = useState<number>(0);
  const [docStatus, setDocStatus] = useState<"pending" | "ready" | "validated" | "delivered" | null>(null);
  const [deliveredTo, setDeliveredTo] = useState<string | null>(null);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { uploads } = useUpload();

  const leftZoom = useZoom();
  const rightZoom = useZoom();
  const leftPan = usePan(leftZoom.zoom > 1);
  const rightPan = usePan(rightZoom.zoom > 1);

  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const jsonPreRef = useRef<HTMLPreElement>(null);
  const pageRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  const structuredPageRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  const leftPageRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  const leftPageInnerScrollRefs = useRef<{ [key: string]: HTMLDivElement }>({});

  const fetchDocumentPages = async () => {
    if (!documentID) return;

    try {
      const { data: pagesData }: { data: PagesData } =
        await getDocumentPages(documentID);
      const pageIds = pagesData.map((page) => page.id);
      setPages(pageIds);
    } catch (error) {
      console.error("Error fetching pages:", error);
    }
  };

  // Close bbox menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        bboxMenuRef.current &&
        !bboxMenuRef.current.contains(e.target as Node)
      ) {
        setShowBboxMenu(false);
      }
      if (
        statusMenuRef.current &&
        !statusMenuRef.current.contains(e.target as Node)
      ) {
        setIsStatusMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStatusChange = async (
    next: "pending" | "ready" | "validated",
  ) => {
    if (!documentID || next === docStatus) {
      setIsStatusMenuOpen(false);
      return;
    }
    setIsUpdatingStatus(true);
    try {
      await adminUpdateDocumentStatus(documentID, next);
      setDocStatus(next);
    } catch (err) {
      console.error("Failed to update status", err);
    } finally {
      setIsUpdatingStatus(false);
      setIsStatusMenuOpen(false);
    }
  };

  useEffect(() => {
    if (!documentID) return;

    let cancelled = false;

    const fetchDocument = async () => {
      try {
        const { data: document }: { data: DocumentData } =
          await getDocument(documentID);
        if (!cancelled) {
          setDocumentName(document.name);
          setDocumentData(document);
          setCanShareText(document.can_share_text ?? true);
          setShowDrawings(document.show_drawings ?? true);
          const locks = {
            text: document.show_text === false,
            molecules: document.show_molecules === false,
            tables: document.show_tables === false,
          };
          setBboxLocks(locks);
          setBboxVisibility((v) => ({
            text: locks.text ? false : v.text,
            molecules: locks.molecules ? false : v.molecules,
            tables: locks.tables ? false : v.tables,
          }));
          if (document.status) setDocStatus(document.status);
          setDeliveredTo(document.delivered_to ?? null);
        }

        const { data: pagesData }: { data: PagesData } =
          await getDocumentPages(documentID);
        if (!cancelled) {
          const pageIds = pagesData.map((page) => page.id);
          setPages(pageIds);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching document:", error);
        }
      }
    };

    fetchDocument();

    return () => {
      cancelled = true;
    };
  }, [documentID]);

  useEffect(() => {
    if (!documentID) return;
    getDocumentComments(documentID)
      .then(({ data }) => setComments(data))
      .catch(() => {});
  }, [documentID]);

  useEffect(() => {
    if (!selectedCommentId) return;
    const comment = comments.find((c) => c.id === selectedCommentId);
    if (!comment) return;
    const pageIndex = pages.indexOf(comment.page_id);

    const scrollInner = () => {
      const innerEl = leftPageInnerScrollRefs.current[comment.page_id];
      if (innerEl) {
        const target =
          comment.y_position * (innerEl.scrollHeight - innerEl.clientHeight);
        innerEl.scrollTo({ top: target, behavior: "smooth" });
      }
    };

    const pageEl = leftPageRefs.current[comment.page_id];
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
      scrollInner();
    } else if (pageIndex >= 0 && leftScrollRef.current) {
      // Page not mounted by the virtualizer yet — jump the scroller and
      // wait for the slot to mount before scrolling within it.
      leftScrollRef.current.scrollTo({
        top: pageIndex * SLOT_HEIGHT,
        behavior: "smooth",
      });
      setTimeout(scrollInner, 250);
    }
    const timer = setTimeout(() => setSelectedCommentId(null), 2000);
    return () => clearTimeout(timer);
  }, [selectedCommentId]);

  const processedUploadIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!documentID) return;

    const completedUpload = uploads.find(
      (u) =>
        u.documentId === documentID &&
        u.status === "complete" &&
        u.mode === "add" &&
        !processedUploadIds.current.has(u.id),
    );

    if (completedUpload) {
      processedUploadIds.current.add(completedUpload.id);
      fetchDocumentPages();
    }
  }, [uploads, documentID]);

  /** Page index currently shown at the top of the given scroller. */
  const getTopPageIndex = (
    scrollRef: React.RefObject<HTMLDivElement | null>,
  ): number => {
    const el = scrollRef.current;
    if (!el) return 0;
    return Math.max(0, Math.round(el.scrollTop / SLOT_HEIGHT));
  };

  /** Scroll the right (data) panel to the page currently shown on the left. */
  const syncRightToLeft = useCallback(() => {
    const target = rightScrollRef.current;
    if (!target) return;
    target.scrollTo({
      top: getTopPageIndex(leftScrollRef) * SLOT_HEIGHT,
      behavior: "smooth",
    });
  }, []);

  /** Scroll the left (image) panel to the page currently shown on the right. */
  const syncLeftToRight = useCallback(() => {
    const target = leftScrollRef.current;
    if (!target) return;
    target.scrollTo({
      top: getTopPageIndex(rightScrollRef) * SLOT_HEIGHT,
      behavior: "smooth",
    });
  }, []);

  const confirmDelete = async () => {
    if (!documentID) {
      setError("Document ID missing");
      return;
    }
    setIsDeleting(true);
    setError(null);

    try {
      await deleteDocument(documentID);
      navigate("/home/dashboard");
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to delete document");
      setIsDeleting(false);
    }
  };

  const handleDeletePageClick = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    setIsDeletePageModalOpen(true);
  };

  const refreshAfterMoleculeChange = (pageId: string) => {
    invalidatePageCache(documentID!, pageId);
    setPageRefreshKeys((prev) => ({
      ...prev,
      [pageId]: (prev[pageId] ?? 0) + 1,
    }));
    setAllPagesJsonData(null);
    setAllPagesRawData([]);
    setObjectsRefreshKey((k) => k + 1);
  };

  const handleMoleculeModalSubmit = async (smiles: string) => {
    if (!documentID || !moleculeModal) return;
    if (moleculeModal.mode === "add") {
      await addMoleculeToPage(documentID, moleculeModal.pageId, smiles);
    } else {
      await updateMoleculeSmiles(
        documentID,
        moleculeModal.pageId,
        moleculeModal.moleculeUuid,
        smiles,
      );
    }
    refreshAfterMoleculeChange(moleculeModal.pageId);
  };

  // Admin: upload a manually-fixed page JSON and merge its molecule objects
  // into this page (combines with the molecules already stored on the page).
  const handleMergeMoleculeFile = async (pageId: string, file: File) => {
    if (!documentID || mergingPageId) return;
    setMergingPageId(pageId);
    try {
      let parsed: unknown;
      try {
        parsed = JSON.parse(await file.text());
      } catch {
        window.alert(`'${file.name}' is not valid JSON.`);
        return;
      }
      const res = await mergeMoleculesIntoPage(documentID, pageId, parsed);
      refreshAfterMoleculeChange(pageId);
      const merged = res.data?.merged ?? 0;
      const skipped = res.data?.skipped ?? 0;
      window.alert(
        `Merged ${merged} molecule${merged === 1 ? "" : "s"}` +
          (skipped
            ? `; skipped ${skipped} already present on this page.`
            : "."),
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ?? err?.message ?? "Merge failed";
      window.alert(`Merge failed: ${msg}`);
    } finally {
      setMergingPageId(null);
    }
  };

  const handleEditMolecule = (
    pageIndex: number,
    moleculeUuid: string,
    currentSmiles: string,
  ) => {
    const pageId = pages[pageIndex];
    if (!pageId) return;
    setMoleculeModal({
      mode: "edit",
      pageId,
      pageIndex,
      moleculeUuid,
      initialSmiles: currentSmiles,
    });
  };

  const handleDeleteMolecule = async (
    pageIndex: number,
    moleculeUuid: string,
  ) => {
    if (!documentID) return;
    const pageId = pages[pageIndex];
    if (!pageId) return;
    if (!window.confirm("Delete this molecule?")) return;
    try {
      await deleteMolecule(documentID, pageId, moleculeUuid);
      refreshAfterMoleculeChange(pageId);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ?? err?.message ?? "Failed to delete";
      window.alert(`Delete failed: ${msg}`);
    }
  };

  const handleBulkDeleteMolecules = async (
    items: { pageIndex: number; moleculeUuid: string }[],
  ) => {
    if (!documentID || items.length === 0) return;
    const resolved = items
      .map((it) => ({
        pageId: pages[it.pageIndex],
        moleculeUuid: it.moleculeUuid,
      }))
      .filter(
        (it): it is { pageId: string; moleculeUuid: string } =>
          Boolean(it.pageId),
      );
    if (resolved.length === 0) return;
    if (
      !window.confirm(
        `Delete ${resolved.length} molecule${resolved.length !== 1 ? "s" : ""}? This cannot be undone.`,
      )
    )
      return;
    // Delete sequentially: molecules on the same page each read-modify-write
    // the page blob, so parallel deletes would race and lose updates.
    const affectedPageIds = new Set<string>();
    let failures = 0;
    for (const { pageId, moleculeUuid } of resolved) {
      try {
        await deleteMolecule(documentID, pageId, moleculeUuid);
        affectedPageIds.add(pageId);
      } catch {
        failures++;
      }
    }
    affectedPageIds.forEach((pid) => refreshAfterMoleculeChange(pid));
    if (failures > 0) {
      window.alert(
        `${failures} molecule${failures !== 1 ? "s" : ""} could not be deleted.`,
      );
    }
  };

  const handleReactionModalSubmit = async (rxnSmiles: string) => {
    if (!documentID || !reactionModal) return;
    if (reactionModal.mode === "add") {
      await addReactionToPage(documentID, reactionModal.pageId, rxnSmiles);
    } else {
      await updateReactionSmiles(
        documentID,
        reactionModal.pageId,
        reactionModal.reactionUuid,
        rxnSmiles,
      );
    }
    refreshAfterMoleculeChange(reactionModal.pageId);
  };

  const handleEditReaction = (
    pageIndex: number,
    reactionUuid: string,
    currentRxnSmiles: string,
  ) => {
    const pageId = pages[pageIndex];
    if (!pageId) return;
    setReactionModal({
      mode: "edit",
      pageId,
      pageIndex,
      reactionUuid,
      initialRxnSmiles: currentRxnSmiles,
    });
  };

  const handleDeleteReaction = async (
    pageIndex: number,
    reactionUuid: string,
  ) => {
    if (!documentID) return;
    const pageId = pages[pageIndex];
    if (!pageId) return;
    if (!window.confirm("Delete this reaction?")) return;
    try {
      await deleteReaction(documentID, pageId, reactionUuid);
      refreshAfterMoleculeChange(pageId);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ?? err?.message ?? "Failed to delete";
      window.alert(`Delete failed: ${msg}`);
    }
  };

  const handleBulkDeleteReactions = async (
    items: { pageIndex: number; reactionUuid: string }[],
  ) => {
    if (!documentID || items.length === 0) return;
    const resolved = items
      .map((it) => ({
        pageId: pages[it.pageIndex],
        reactionUuid: it.reactionUuid,
      }))
      .filter(
        (it): it is { pageId: string; reactionUuid: string } =>
          Boolean(it.pageId),
      );
    if (resolved.length === 0) return;
    if (
      !window.confirm(
        `Delete ${resolved.length} reaction${resolved.length !== 1 ? "s" : ""}? This cannot be undone.`,
      )
    )
      return;
    // Delete sequentially: reactions on the same page each read-modify-write
    // the page blob, so parallel deletes would race and lose updates.
    const affectedPageIds = new Set<string>();
    let failures = 0;
    for (const { pageId, reactionUuid } of resolved) {
      try {
        await deleteReaction(documentID, pageId, reactionUuid);
        affectedPageIds.add(pageId);
      } catch {
        failures++;
      }
    }
    affectedPageIds.forEach((pid) => refreshAfterMoleculeChange(pid));
    if (failures > 0) {
      window.alert(
        `${failures} reaction${failures !== 1 ? "s" : ""} could not be deleted.`,
      );
    }
  };

  const handleRestructurePage = async (pageId: string) => {
    if (!documentID || restructuringPageId) return;
    setRestructuringPageId(pageId);
    try {
      await restructurePage(documentID, pageId);
      invalidatePageCache(documentID, pageId);
      setPageRefreshKeys((prev) => ({
        ...prev,
        [pageId]: (prev[pageId] ?? 0) + 1,
      }));
    } catch (err: any) {
      console.error("Restructure failed:", err);
      const msg =
        err?.response?.data?.detail ?? err?.message ?? "Restructure failed";
      window.alert(`Restructure failed: ${msg}`);
    } finally {
      setRestructuringPageId(null);
    }
  };

  const confirmDeletePage = async () => {
    if (!documentID) {
      setError("Document ID missing");
      return;
    }

    if (currentPageIndex < 0 || currentPageIndex >= pages.length) {
      setError("Invalid page selection");
      return;
    }

    setIsDeletingPage(true);
    setError(null);

    try {
      if (pages.length === 1) {
        await deleteDocument(documentID);
        navigate("/home/dashboard");
        return;
      }

      const pageIdToDelete = pages[currentPageIndex]!;
      await deletePage(documentID!, pageIdToDelete);
      setPages((prevPages) =>
        prevPages.filter((_, index) => index !== currentPageIndex),
      );
      setIsDeletePageModalOpen(false);
      setIsDeletingPage(false);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to delete page");
      setIsDeletingPage(false);
    }
  };

  const handleAddPagesModalClose = (isOpen: boolean) => {
    setIsAddPagesModalOpen(isOpen);

    if (!isOpen) {
      fetchDocumentPages();
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingName(true);
    setEditingName(documentName);
  };

  const handleSaveEdit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!documentID) {
      setError("Document ID missing");
      return;
    }
    setError(null);

    try {
      await renameDocument(documentID, editingName);
      setDocumentName(editingName);
      setIsEditingName(false);
      setEditingName("");
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to rename document");
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingName(false);
    setEditingName("");
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || pages.length === 0) return;

    const query = searchQuery.toLowerCase();
    const allOccurrences: Array<{ pageId: string; occurrenceIndex: number }> =
      [];

    for (let i = 0; i < pages.length; i++) {
      const pageId = pages[i];
      try {
        const { data } = await getPageData(documentID!, pageId!);
        const readableDataString = JSON.stringify(
          data.readable_data,
        ).toLowerCase();

        let position = 0;
        let occurrenceIndex = 0;
        while (
          (position = readableDataString.indexOf(query, position)) !== -1
        ) {
          allOccurrences.push({ pageId: pageId!, occurrenceIndex });
          position += query.length;
          occurrenceIndex++;
        }
      } catch (error) {
        console.error(`Error searching page ${pageId}:`, error);
      }
    }

    setSearchResults(allOccurrences);
    setCurrentResultIndex(0);
    setActiveSearchQuery(searchQuery);

    if (allOccurrences.length > 0) {
      setTimeout(() => {
        scrollToResult(allOccurrences[0]!.pageId);
      }, 150);
    }
  };

  const scrollToResult = (pageId: string) => {
    if (!rightScrollRef.current) return;
    const pageElement = pageRefs.current[pageId];

    if (pageElement) {
      pageElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      const pageIndex = pages.indexOf(pageId);
      if (pageIndex >= 0) {
        rightScrollRef.current.scrollTo({
          top: pageIndex * SLOT_HEIGHT,
          behavior: "smooth",
        });
      }
    }
  };

  /** Scroll the left (original image) panel to a specific page index. */
  const scrollToLeftPage = useCallback(
    (pageIndex: number) => {
      const pageId = pages[pageIndex];
      if (!pageId) return;
      const el = leftPageRefs.current[pageId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (leftScrollRef.current) {
        leftScrollRef.current.scrollTo({
          top: pageIndex * SLOT_HEIGHT,
          behavior: "smooth",
        });
      }
    },
    [pages],
  );

  const navigateToNextResult = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    scrollToResult(searchResults[nextIndex]!.pageId);
  };

  const navigateToPrevResult = () => {
    if (searchResults.length === 0) return;
    const prevIndex =
      currentResultIndex === 0
        ? searchResults.length - 1
        : currentResultIndex - 1;
    setCurrentResultIndex(prevIndex);
    scrollToResult(searchResults[prevIndex]!.pageId);
  };

  const clearSearch = () => {
    setSearchResults([]);
    setCurrentResultIndex(0);
    setActiveSearchQuery("");
  };

  const fetchAllPagesJson = async () => {
    if (!documentID || pages.length === 0) {
      return null;
    }

    try {
      const totalPages = pages.length;
      const offsets: number[] = [];
      for (let off = 0; off < totalPages; off += PAGE_BATCH_SIZE_RAW) {
        offsets.push(off);
      }
      const batchResults: (
        | { id: string; raw_data: any; readable_data: any }[]
        | undefined
      )[] = new Array(offsets.length);

      let nextBatchIdx = 0;
      const worker = async () => {
        while (true) {
          const idx = nextBatchIdx++;
          if (idx >= offsets.length) return;
          const offset = offsets[idx]!;
          const { data } = await getAllPagesData(documentID, {
            includeRaw: true,
            offset,
            limit: PAGE_BATCH_SIZE_RAW,
          });
          batchResults[idx] = data as {
            id: string;
            raw_data: any;
            readable_data: any;
          }[];
        }
      };

      await Promise.all(
        Array.from({ length: PAGE_FETCH_CONCURRENCY }, () => worker()),
      );

      const collected = batchResults.flatMap((b) => b ?? []);
      const allPagesData = collected.map((p, index) => ({
        page_number: index + 1,
        page_id: p.id,
        raw_data: p.raw_data,
        readable_data: p.readable_data,
      }));

      return {
        document_name: documentName,
        document_id: documentID,
        total_pages: allPagesData.length,
        pages: allPagesData,
      };
    } catch (err: any) {
      console.error("Error fetching JSON:", err);
      setError(err.message || "Failed to fetch JSON");
      return null;
    }
  };

  const [jsonSearchQuery, setJsonSearchQuery] = useState<string>("");
  const [jsonSearchResults, setJsonSearchResults] = useState<number[]>([]);
  const [jsonCurrentResultIdx, setJsonCurrentResultIdx] = useState<number>(0);

  // Stringifying 600+ pages with transformToDigitalFormat is expensive
  // (multi-second main-thread block). Memoize so it runs once per data
  // change, not on every keystroke / zoom / pan.
  const jsonViewString = useMemo<string | null>(() => {
    if (!allPagesJsonData) return null;
    return JSON.stringify(
      buildRawJsonObject(allPagesJsonData.document_name, allPagesJsonData.pages ?? []),
      null,
      2,
    );
  }, [allPagesJsonData]);


  const handleJsonSearch = () => {
    const pre = jsonPreRef.current;
    if (!pre || !jsonSearchQuery.trim()) return;
    const text = pre.textContent ?? "";
    const query = jsonSearchQuery.toLowerCase();
    const positions: number[] = [];
    let pos = 0;
    while (true) {
      const found = text.toLowerCase().indexOf(query, pos);
      if (found === -1) break;
      positions.push(found);
      pos = found + 1;
    }
    setJsonSearchResults(positions);
    setJsonCurrentResultIdx(0);
  };

  const navigateJsonNext = () => {
    if (jsonSearchResults.length === 0) return;
    setJsonCurrentResultIdx((i) => (i + 1) % jsonSearchResults.length);
  };

  const navigateJsonPrev = () => {
    if (jsonSearchResults.length === 0) return;
    setJsonCurrentResultIdx((i) =>
      i === 0 ? jsonSearchResults.length - 1 : i - 1,
    );
  };

  const clearJsonSearch = () => {
    setJsonSearchResults([]);
    setJsonCurrentResultIdx(0);
    setJsonSearchQuery("");
  };

  useEffect(() => {
    if (jsonSearchResults.length === 0) return;
    const el = jsonPreRef.current?.querySelector<HTMLElement>(
      "[data-json-current-match]",
    );
    el?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
  }, [jsonCurrentResultIdx, jsonSearchResults]);

  const jsonNavOccurrenceRef = useRef<Record<string, number>>({});

  const scrollJsonToKey = (key: string) => {
    const pre = jsonPreRef.current;
    const scroller = rightScrollRef.current;
    if (!pre || !scroller) return;
    const text = pre.textContent ?? "";
    const search = `"${key}":`;

    const occurrences: number[] = [];
    let pos = 0;
    while (true) {
      const found = text.indexOf(search, pos);
      if (found === -1) break;
      occurrences.push(found);
      pos = found + 1;
    }
    if (occurrences.length === 0) return;

    const prev = jsonNavOccurrenceRef.current[key] ?? -1;
    const next = (prev + 1) % occurrences.length;
    jsonNavOccurrenceRef.current[key] = next;

    const charIdx = occurrences[next];
    const linesBefore = text.substring(0, charIdx).split("\n").length - 1;
    scroller.scrollTop = pre.offsetTop + linesBefore * 20;
  };

  // Map of line-number → page_number within jsonViewString, used to scroll
  // the left (image) panel to whichever page the JSON is currently showing.
  const jsonPageLineMap = useMemo<Array<{ line: number; pageNumber: number }>>(
    () => {
      if (!jsonViewString) return [];
      const map: Array<{ line: number; pageNumber: number }> = [];
      const lines = jsonViewString.split("\n");
      const re = /"page_number":\s*(\d+)/;
      for (let i = 0; i < lines.length; i++) {
        const m = lines[i]!.match(re);
        if (m) map.push({ line: i, pageNumber: parseInt(m[1]!, 10) });
      }
      return map;
    },
    [jsonViewString],
  );

  const lastJsonScrolledPage = useRef<number | null>(null);

  const handleJsonScroll = () => {
    const pre = jsonPreRef.current;
    const scroller = rightScrollRef.current;
    if (!pre || !scroller || jsonPageLineMap.length === 0) return;
    const topPx = scroller.scrollTop - pre.offsetTop;
    const topLine = Math.max(0, Math.floor(topPx / 20));
    let pageNumber: number | null = null;
    for (let i = 0; i < jsonPageLineMap.length; i++) {
      const entry = jsonPageLineMap[i]!;
      if (entry.line <= topLine) pageNumber = entry.pageNumber;
      else break;
    }
    if (pageNumber == null) return;
    if (lastJsonScrolledPage.current === pageNumber) return;
    lastJsonScrolledPage.current = pageNumber;

    const pageIndex = pageNumber - 1;
    const pageId = pages[pageIndex];
    if (!pageId) return;
    const el = leftPageRefs.current[pageId];
    if (el) {
      el.scrollIntoView({ block: "start" });
    } else if (leftScrollRef.current) {
      leftScrollRef.current.scrollTo({ top: pageIndex * SLOT_HEIGHT });
    }
  };

  const ensureAllPagesJson = (): Promise<any> => {
    if (allPagesJsonData) return Promise.resolve(allPagesJsonData);
    if (inFlightJsonRef.current) return inFlightJsonRef.current;
    setJsonLoading(true);
    setJsonError(false);
    const p = fetchAllPagesJson().then((data) => {
      inFlightJsonRef.current = null;
      if (data) {
        setAllPagesJsonData(data);
        if (data.pages) {
          setAllPagesRawData(data.pages.map((page: any) => page.raw_data));
        }
      } else {
        setJsonError(true);
      }
      setJsonLoading(false);
      return data;
    });
    inFlightJsonRef.current = p;
    return p;
  };

  const handleToggleJsonView = () => {
    setActiveTab("json");
    void ensureAllPagesJson();
  };

  const handleToggleStructuredView = () => {
    setActiveTab("positioned");
  };

  const handleToggleChemistryView = () => {
    setActiveTab("chemistry");
  };

  const handleExportRawJson = async () => {
    if (!documentID || pages.length === 0) {
      setError("No data to export");
      return;
    }

    try {
      const exportData = await ensureAllPagesJson();

      if (!exportData) {
        setError("Failed to fetch data for export");
        return;
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentName.replace(/[^a-z0-9]/gi, "_")}_raw.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error exporting raw JSON:", err);
      setError(err.message || "Failed to export raw JSON");
    }
  };

  const handleExportJson = async () => {
    if (!documentID || pages.length === 0) {
      setError("No data to export");
      return;
    }

    try {
      const exportData = await ensureAllPagesJson();

      if (!exportData) {
        setError("Failed to fetch data for export");
        return;
      }

      const exportDataWithTimestamp = {
        document_name: exportData.document_name,
        total_pages: exportData.total_pages,
        exported_at: new Date().toISOString(),
        pages: (exportData.pages ?? []).map((p: any) => ({
          page_number: p.page_number,
          ...transformToDigitalFormat(p.readable_data, p.raw_data),
        })),
      };

      const blob = new Blob(
        [JSON.stringify(exportDataWithTimestamp, null, 2)],
        {
          type: "application/json",
        },
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentName.replace(/[^a-z0-9]/gi, "_")}_export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error exporting JSON:", err);
      setError(err.message || "Failed to export JSON");
    }
  };

  const handleDownloadPageJson = async (pageId: string, pageIndex: number) => {
    if (!documentID) {
      setError("Document ID missing");
      return;
    }

    try {
      const { data } = await getPageData(documentID, pageId);

      const pageData = {
        document_name: documentName,
        page_number: pageIndex + 1,
        exported_at: new Date().toISOString(),
        ...transformToDigitalFormat(data.readable_data, data.raw_data),
      };

      const blob = new Blob([JSON.stringify(pageData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentName.replace(/[^a-z0-9]/gi, "_")}_page_${
        pageIndex + 1
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(`Error downloading page ${pageIndex + 1} JSON:`, err);
      setError(err.message || "Failed to download page JSON");
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <UserNavbar />

      {documentData === null && <DocumentViewSkeleton />}

      {/* Mobile tab switcher */}
      <div
        className={`md:hidden flex bg-[#303a4a] border-b border-[#3d4552]${documentData === null ? " hidden" : ""}`}
      >
        <button
          onClick={() => setMobileView("image")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            mobileView === "image"
              ? "bg-[#1d365d] text-white"
              : "text-[#d6d9da] hover:bg-[#3d4552]"
          }`}
        >
          Document
        </button>
        <button
          onClick={() => setMobileView("data")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            mobileView === "data"
              ? "bg-[#1d365d] text-white"
              : "text-[#d6d9da] hover:bg-[#3d4552]"
          }`}
        >
          Extracted Data
        </button>
      </div>

      <div
        className={`relative flex flex-col md:flex-row bg-white text-gray-800 font-sans flex-1 overflow-hidden${documentData === null ? " hidden" : ""}`}
      >
        {/* Sync arrows on the divider — like Overleaf */}
        {activeTab === "positioned" && pages.length > 0 && (
          <div className="hidden md:flex flex-col gap-1 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
            <Tooltip text="Jump data view to this page" position="right">
              <button
                onClick={syncRightToLeft}
                className="pointer-events-auto flex items-center justify-center w-6 h-6 bg-white border border-gray-300 shadow-md rounded-full hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                <ChevronRight size={14} className="text-gray-700" />
              </button>
            </Tooltip>
            <Tooltip text="Jump image to this page" position="right">
              <button
                onClick={syncLeftToRight}
                className="pointer-events-auto flex items-center justify-center w-6 h-6 bg-white border border-gray-300 shadow-md rounded-full hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                <ChevronLeft size={14} className="text-gray-700" />
              </button>
            </Tooltip>
          </div>
        )}
        {/* left half */}
        <div
          className={`w-full md:w-1/2 flex flex-col border-r border-gray-200 overflow-hidden ${mobileView !== "image" ? "hidden md:flex" : "flex"}`}
        >
          {/* toolbar */}
          <div className="bg-[#303a4a] border-b border-gray-200 py-1 px-2 md:px-4 flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
              <div className="flex items-center space-x-2 pr-2 md:pr-3 border-r border-gray-600 shrink-0">
                <Tooltip text="Go back" position="right">
                  <button
                    onClick={() => navigate(-1)}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                  >
                    <House size={18} className="md:w-5 md:h-5 text-[#d6d9da]" />
                  </button>
                </Tooltip>
              </div>

              {isEditingName ? (
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] text-sm min-w-0 flex-1"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveEdit}
                    className="text-green-500 hover:text-green-600 font-medium text-sm shrink-0"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="text-white hover:text-gray-200 font-medium text-sm shrink-0"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <h1
                    className="font-medium text-slate-700 truncate text-white text-sm md:text-base min-w-0"
                    title={documentName}
                  >
                    {documentName}
                  </h1>
                  <Tooltip text="Rename" position="right">
                    <button
                      onClick={handleEditClick}
                      className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors shrink-0"
                    >
                      <Pencil
                        size={16}
                        className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                      />
                    </button>
                  </Tooltip>

                  {isAdmin && docStatus && (
                    docStatus === "delivered" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5 shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                        Delivered{deliveredTo ? ` · ${deliveredTo}` : ""}
                      </span>
                    ) : (
                      <div className="relative shrink-0" ref={statusMenuRef}>
                        <button
                          disabled={isUpdatingStatus}
                          onClick={() => setIsStatusMenuOpen((v) => !v)}
                          className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-0.5 border transition-colors ${
                            docStatus === "ready"
                              ? "text-green-700 bg-green-50 border-green-200 hover:bg-green-100"
                              : docStatus === "validated"
                              ? "text-violet-700 bg-violet-50 border-violet-200 hover:bg-violet-100"
                              : "text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100"
                          }`}
                        >
                          {isUpdatingStatus ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <span
                              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                docStatus === "ready"
                                  ? "bg-green-500"
                                  : docStatus === "validated"
                                  ? "bg-violet-500"
                                  : "bg-amber-400"
                              }`}
                            />
                          )}
                          {docStatus === "ready"
                            ? "Ready"
                            : docStatus === "validated"
                            ? "Action required"
                            : "Pending"}
                          <ChevronDown
                            size={10}
                            className={`transition-transform ${isStatusMenuOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                        {isStatusMenuOpen && (
                          <div className="absolute left-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-30 overflow-hidden">
                            {(["pending", "ready", "validated"] as const).map((s) => (
                              <button
                                key={s}
                                onClick={() => handleStatusChange(s)}
                                className={`flex items-center gap-2 w-full px-3 py-2 text-xs font-medium transition-colors hover:bg-gray-50 ${s === docStatus ? "opacity-40 cursor-default" : ""}`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                    s === "ready"
                                      ? "bg-green-500"
                                      : s === "validated"
                                      ? "bg-violet-500"
                                      : "bg-amber-400"
                                  }`}
                                />
                                <span
                                  className={
                                    s === "ready"
                                      ? "text-green-700"
                                      : s === "validated"
                                      ? "text-violet-700"
                                      : "text-amber-700"
                                  }
                                >
                                  {s === "ready"
                                    ? "Ready"
                                    : s === "validated"
                                    ? "Action required"
                                    : "Pending"}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </>
              )}
            </div>

            <div className="flex items-center space-x-1 md:space-x-2 shrink-0">
              {/* bbox visibility dropdown */}
              <div className="relative" ref={bboxMenuRef}>
                <Tooltip text="Toggle overlays" position="left">
                  <button
                    onClick={() => setShowBboxMenu((v) => !v)}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                  >
                    <SlidersHorizontal
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
                {showBboxMenu && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-[#303a4a] border border-gray-600 rounded-lg shadow-xl py-1 w-48">
                    {(
                      [
                        ["text", "Text"],
                        ["molecules", "Molecules"],
                        ["tables", "Tables"],
                      ] as const
                    ).map(([key, label]) => {
                      const locked = bboxLocks[key];
                      return (
                        <label
                          key={key}
                          title={
                            locked
                              ? "Disabled by organisation settings"
                              : undefined
                          }
                          className={`flex items-center justify-between px-3 py-2 select-none ${
                            locked
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-[#3d4552] cursor-pointer"
                          }`}
                        >
                          <span className="text-sm text-[#d6d9da]">
                            {label}
                          </span>
                          <div
                            onClick={() => {
                              if (locked) return;
                              setBboxVisibility((v) => ({
                                ...v,
                                [key]: !v[key],
                              }));
                            }}
                            className={`relative w-9 h-5 rounded-full transition-colors ${
                              bboxVisibility[key]
                                ? "bg-[#1d365d]"
                                : "bg-gray-600"
                            } ${locked ? "cursor-not-allowed" : ""}`}
                          >
                            <div
                              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                                bboxVisibility[key]
                                  ? "translate-x-4"
                                  : "translate-x-0"
                              }`}
                            />
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* zoom controls */}
              <div className="flex items-center space-x-0.5 border-r border-gray-600 pr-1 md:pr-2 mr-0.5">
                <Tooltip text="Zoom out" position="left">
                  <button
                    onClick={leftZoom.zoomOut}
                    disabled={!leftZoom.canZoomOut}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ZoomOut
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
                <button
                  onClick={leftZoom.resetZoom}
                  className="text-[#d6d9da] text-xs font-mono w-10 text-center hover:bg-[#3d4552] rounded px-1 py-0.5 transition-colors"
                  title="Reset zoom"
                >
                  {Math.round(leftZoom.zoom * 100)}%
                </button>
                <Tooltip text="Zoom in" position="left">
                  <button
                    onClick={leftZoom.zoomIn}
                    disabled={!leftZoom.canZoomIn}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ZoomIn
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
              </div>

              <Tooltip
                text={commentMode ? "Exit comment mode" : "Add comments"}
                position="left"
              >
                <button
                  onClick={() => setCommentMode((v) => !v)}
                  className={`p-1.5 md:p-2 rounded-md transition-colors ${commentMode ? "bg-amber-500 hover:bg-amber-600" : "hover:bg-[#3d4552]"}`}
                >
                  <MessageSquarePlus
                    size={18}
                    className="md:w-5 md:h-5 text-[#d6d9da]"
                  />
                </button>
              </Tooltip>

              <Tooltip text="Add pages" position="left">
                <button
                  onClick={() => setIsAddPagesModalOpen(true)}
                  className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                >
                  <FilePlus
                    size={18}
                    className="md:w-5 md:h-5 text-[#d6d9da]"
                  />
                </button>
              </Tooltip>

              <Tooltip text="Delete document" position="left">
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={isDeleting}
                  className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors"
                >
                  <Trash size={18} className="md:w-5 md:h-5 text-[#d6d9da]" />
                </button>
              </Tooltip>
            </div>
          </div>

          {/* image + sidebar */}
          <div className="flex flex-1 overflow-hidden">
            <CommentSidebar
              comments={comments}
              documentId={documentID!}
              currentUserEmail={user?.email ?? null}
              pendingPin={pendingPin}
              selectedCommentId={selectedCommentId}
              pages={pages}
              onCommentsChanged={setComments}
              onPendingPinClear={() => setPendingPin(null)}
              onSelectComment={setSelectedCommentId}
            />
            {/* image display */}
            <div
              ref={leftScrollRef}
              className="flex-grow bg-gray-100 overflow-y-auto panel-scroll"
            >
              <VirtualizedPages
                pages={pages}
                scrollRef={leftScrollRef}
                slotHeight={SLOT_HEIGHT}
                renderSlot={(pageId, index) => (
                  <div
                    ref={(el) => {
                      if (el) {
                        leftPageRefs.current[pageId] = el;
                      } else {
                        delete leftPageRefs.current[pageId];
                      }
                    }}
                    className="px-2 md:px-5 relative pt-3 md:pt-5"
                  >
                    {/* page header — never zoomed */}
                    <div className="mb-2 text-sm font-semibold text-gray-500 text-center flex items-center justify-center gap-2">
                      <span>Page {index + 1}</span>

                      {isAdmin && (
                        <button
                          onClick={() => handleRestructurePage(pageId)}
                          disabled={restructuringPageId !== null}
                          className="px-2 py-0.5 text-xs font-medium rounded border border-[#1d365d] text-[#1d365d] hover:bg-[#1d365d] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                          title="Re-run structuring + tables for this page (admin)"
                        >
                          <RefreshCw
                            size={12}
                            className={
                              restructuringPageId === pageId
                                ? "animate-spin"
                                : ""
                            }
                          />
                          {restructuringPageId === pageId
                            ? "Restructuring…"
                            : "Restructure"}
                        </button>
                      )}

                      {isAdmin && (
                        <button
                          onClick={() =>
                            setMoleculeModal({
                              mode: "add",
                              pageId,
                              pageIndex: index,
                            })
                          }
                          className="px-2 py-0.5 text-xs font-medium rounded border border-[#1d365d] text-[#1d365d] hover:bg-[#1d365d] hover:text-white transition-colors flex items-center gap-1"
                          title="Add a missing molecule's SMILES to this page (admin)"
                        >
                          <FlaskConical size={12} />
                          Add molecule
                        </button>
                      )}

                      {isAdmin && (
                        <label
                          className={`px-2 py-0.5 text-xs font-medium rounded border border-[#1d365d] text-[#1d365d] hover:bg-[#1d365d] hover:text-white transition-colors flex items-center gap-1 ${
                            mergingPageId !== null
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                          title="Upload a fixed page JSON and merge its molecules into this page (admin)"
                        >
                          {mergingPageId === pageId ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Upload size={12} />
                          )}
                          {mergingPageId === pageId ? "Merging…" : "Upload fix"}
                          <input
                            type="file"
                            accept="application/json,.json"
                            className="sr-only"
                            disabled={mergingPageId !== null}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              if (file) handleMergeMoleculeFile(pageId, file);
                            }}
                          />
                        </label>
                      )}

                      {isAdmin && (
                        <button
                          onClick={() =>
                            setReactionModal({
                              mode: "add",
                              pageId,
                              pageIndex: index,
                            })
                          }
                          className="px-2 py-0.5 text-xs font-medium rounded border border-[#1d365d] text-[#1d365d] hover:bg-[#1d365d] hover:text-white transition-colors flex items-center gap-1"
                          title="Add a missing reaction's SMILES to this page (admin)"
                        >
                          <FlaskConical size={12} />
                          Add reaction
                        </button>
                      )}

                      <button
                        onClick={() => handleDeletePageClick(index)}
                        className="p-1 hover:bg-red-100 rounded transition-colors"
                        title="Delete this page"
                      >
                        <Trash size={14} className="text-red-600" />
                      </button>
                    </div>

                    {/* pannable + zoomable content */}
                    <div
                      ref={(el) => {
                        if (el) {
                          leftPageInnerScrollRefs.current[pageId] = el;
                        } else {
                          delete leftPageInnerScrollRefs.current[pageId];
                        }
                      }}
                      className="h-[60vh] md:h-[800px] overflow-auto"
                      style={{
                        cursor: commentMode ? "crosshair" : leftPan.cursor,
                      }}
                      {...(commentMode ? {} : leftPan.panHandlers)}
                    >
                      {/* spacer grows with zoom → real scrollable area */}
                      <div
                        style={{
                          width: `${leftZoom.zoom * 100}%`,
                          height: `${leftZoom.zoom * 100}%`,
                          minWidth: "100%",
                          minHeight: "100%",
                          position: "relative",
                        }}
                      >
                        {/* content: 1/zoom of spacer = original container size, then scaled visually */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: `${(1 / leftZoom.zoom) * 100}%`,
                            height: `${(1 / leftZoom.zoom) * 100}%`,
                            transform: `scale(${leftZoom.zoom})`,
                            transformOrigin: "top left",
                          }}
                        >
                          <DisplayPredictions
                            key={`${pageId}-${pageRefreshKeys[pageId] ?? 0}`}
                            documentID={documentID!}
                            pageID={pageId}
                            mode="image-only"
                            selectedTerm={selectedTerm}
                            onHoverObject={setHoveredObjectUuid}
                            highlightedObjectUuid={hoveredObjectUuid}
                            bboxVisibility={bboxVisibility}
                            {...(hoveredBboxOverlay?.pageIndex === index && {
                              highlightedBbox: hoveredBboxOverlay.bbox,
                            })}
                          />
                          <CommentLayer
                            pageId={pageId}
                            comments={comments}
                            zoom={leftZoom.zoom}
                            commentMode={commentMode}
                            selectedCommentId={selectedCommentId}
                            onDocumentClick={(xFrac, yFrac) =>
                              setPendingPin({ pageId, xFrac, yFrac })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
          {/* end flex wrapper: sidebar + image */}
        </div>

        {/* right half */}
        <div
          className={`w-full md:w-1/2 flex flex-col overflow-hidden ${mobileView !== "data" ? "hidden md:flex" : "flex"}`}
        >
          {/* toolbar */}
          <div className="bg-[#303a4a] border-b border-gray-200 py-1 px-2 md:px-4 flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
              <h1
                className="font-medium text-slate-700 truncate text-white text-sm md:text-base hidden sm:block"
                title={documentName}
              >
                Extracted Data
              </h1>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3">
              {/* zoom controls */}
              <div className="flex items-center space-x-0.5 border-r border-gray-600 pr-1 md:pr-2">
                <Tooltip text="Zoom out" position="left">
                  <button
                    onClick={rightZoom.zoomOut}
                    disabled={!rightZoom.canZoomOut}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ZoomOut
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
                <button
                  onClick={rightZoom.resetZoom}
                  className="text-[#d6d9da] text-xs font-mono w-10 text-center hover:bg-[#3d4552] rounded px-1 py-0.5 transition-colors"
                  title="Reset zoom"
                >
                  {Math.round(rightZoom.zoom * 100)}%
                </button>
                <Tooltip text="Zoom in" position="left">
                  <button
                    onClick={rightZoom.zoomIn}
                    disabled={!rightZoom.canZoomIn}
                    className="p-1.5 md:p-2 rounded-md hover:bg-[#3d4552] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ZoomIn
                      size={16}
                      className="md:w-[18px] md:h-[18px] text-[#d6d9da]"
                    />
                  </button>
                </Tooltip>
              </div>

              {/* view mode buttons */}
              <div className="flex items-center space-x-0.5 md:space-x-1 bg-[#3d4552] rounded-lg p-0.5 md:p-1">
                <button
                  onClick={handleToggleStructuredView}
                  className={`px-2 md:px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                    activeTab === "positioned"
                      ? "bg-[#1d365d] text-white"
                      : "text-[#d6d9da] hover:bg-[#303a4a]"
                  }`}
                >
                  <span className="hidden sm:inline">Preview</span>
                  <span className="sm:hidden">Pre</span>
                </button>
                <button
                  onClick={handleToggleChemistryView}
                  className={`px-2 md:px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                    activeTab === "chemistry"
                      ? "bg-[#1d365d] text-white"
                      : "text-[#d6d9da] hover:bg-[#303a4a]"
                  }`}
                >
                  <span>Objects</span>
                </button>
                <button
                  onClick={handleToggleJsonView}
                  className={`px-2 md:px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                    activeTab === "json"
                      ? "bg-[#1d365d] text-white"
                      : "text-[#d6d9da] hover:bg-[#303a4a]"
                  }`}
                >
                  <span className="hidden sm:inline">Raw JSON</span>
                  <span className="sm:hidden">JSON</span>
                </button>
              </div>

            </div>
          </div>

          {/* search bar — preview tab only */}
          {activeTab === "positioned" && (
            <div className="bg-white border-b border-gray-200 px-2 md:px-4 py-2 md:py-3">
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    className="w-full h-9 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] text-gray-700 placeholder-gray-400 transition-colors"
                  />
                </div>

                <PrimaryButton
                  onClick={handleSearch}
                  disabled={!searchQuery.trim()}
                  className="!px-3 md:!px-5"
                >
                  <span className="hidden sm:inline">Search</span>
                  <Search size={16} className="sm:hidden" />
                </PrimaryButton>
              </div>

              {searchResults.length > 0 && (
                <div className="mt-2 md:mt-3 flex items-center justify-between gap-2 md:gap-3 bg-gray-50 border border-gray-200 rounded-lg px-2 md:px-4 py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <span className="text-xs md:text-sm text-gray-700 font-medium whitespace-nowrap">
                      {currentResultIndex + 1}/{searchResults.length}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={navigateToPrevResult}
                        className="p-1 md:p-1.5 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={searchResults.length === 0}
                        title="Previous result"
                      >
                        <ChevronUp size={16} className="text-gray-700" />
                      </button>
                      <button
                        onClick={navigateToNextResult}
                        className="p-1 md:p-1.5 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={searchResults.length === 0}
                        title="Next result"
                      >
                        <ChevronDown size={16} className="text-gray-700" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={clearSearch}
                    className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Close search results"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* search bar — JSON tab only */}
          {activeTab === "json" && (
            <div className="bg-white border-b border-gray-200 px-2 md:px-4 py-2 md:py-3">
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    placeholder="Search JSON..."
                    value={jsonSearchQuery}
                    onChange={(e) => setJsonSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleJsonSearch();
                    }}
                    className="w-full h-9 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] text-gray-700 placeholder-gray-400 transition-colors"
                  />
                </div>

                <PrimaryButton
                  onClick={handleJsonSearch}
                  disabled={!jsonSearchQuery.trim()}
                  className="!px-3 md:!px-5"
                >
                  <span className="hidden sm:inline">Search</span>
                  <Search size={16} className="sm:hidden" />
                </PrimaryButton>
              </div>

              {jsonSearchResults.length > 0 && (
                <div className="mt-2 md:mt-3 flex items-center justify-between gap-2 md:gap-3 bg-gray-50 border border-gray-200 rounded-lg px-2 md:px-4 py-2">
                  <div className="flex items-center gap-2 md:gap-3">
                    <span className="text-xs md:text-sm text-gray-700 font-medium whitespace-nowrap">
                      {jsonCurrentResultIdx + 1}/{jsonSearchResults.length}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={navigateJsonPrev}
                        className="p-1 md:p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Previous result"
                      >
                        <ChevronUp size={16} className="text-gray-700" />
                      </button>
                      <button
                        onClick={navigateJsonNext}
                        className="p-1 md:p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Next result"
                      >
                        <ChevronDown size={16} className="text-gray-700" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={clearJsonSearch}
                    className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Clear search"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* display output */}
          <div
            ref={rightScrollRef}
            className="flex-grow overflow-y-auto bg-white panel-scroll"
            onScroll={activeTab === "json" ? handleJsonScroll : undefined}
          >
            {activeTab === "json" ? (
              /* JSON view — single pannable block */
              <>
                <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-200 bg-white sticky top-0 z-10">
                  {([
                    { key: "text", label: "Text" },
                    { key: "molecules", label: "Molecules" },
                    { key: "reactions", label: "Reactions" },
                    { key: "tables", label: "Tables" },
                  ] as const).map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => scrollJsonToKey(key)}
                      className="px-3 py-1 text-xs font-medium rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                  <div className="ml-auto">
                    <button
                      onClick={handleExportJson}
                      className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    >
                      <Download size={13} />
                      Download JSON
                    </button>
                  </div>
                </div>
              {jsonViewString ? (
                <div
                  className="overflow-auto"
                  style={{ cursor: rightPan.cursor }}
                  {...rightPan.panHandlers}
                >
                  <div
                    style={{
                      width: `${rightZoom.zoom * 100}%`,
                      minWidth: "100%",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        width: `${(1 / rightZoom.zoom) * 100}%`,
                        transform: `scale(${rightZoom.zoom})`,
                        transformOrigin: "top left",
                      }}
                    >
                      <div className="p-4">
                        <pre ref={jsonPreRef} className="bg-slate-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto">
                          {renderJsonWithHighlights(
                            jsonViewString,
                            jsonSearchResults.length > 0 ? jsonSearchQuery : "",
                            jsonCurrentResultIdx,
                          )}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              ) : jsonError ? (
                <div className="flex flex-col items-center justify-center h-48 gap-3 text-sm text-gray-500">
                  <span>Failed to load JSON.</span>
                  <button
                    onClick={() => {
                      setJsonError(false);
                      void ensureAllPagesJson();
                    }}
                    className="px-3 py-1 bg-[#1d365d] text-white rounded hover:bg-[#152848] transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 h-48 text-sm text-gray-400">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-[#1d365d] rounded-full animate-spin" />
                  {jsonLoading ? "Loading raw JSON…" : "Loading…"}
                </div>
              )}
              </>
            ) : activeTab === "chemistry" ? (
              <ChemistryDataLoader
                documentID={documentID!}
                pages={pages}
                canShareText={canShareText}
                onHoverObject={setHoveredObjectUuid}
                hoveredObjectUuid={hoveredObjectUuid}
                onScrollToPage={scrollToLeftPage}
                onHoverBbox={setHoveredBboxOverlay}
                refreshKey={objectsRefreshKey}
                {...(isAdmin && {
                  onEditMolecule: handleEditMolecule,
                  onDeleteMolecule: handleDeleteMolecule,
                  onBulkDeleteMolecules: handleBulkDeleteMolecules,
                  onEditReaction: handleEditReaction,
                  onDeleteReaction: handleDeleteReaction,
                  onBulkDeleteReactions: handleBulkDeleteReactions,
                })}
              />
            ) : (
              <VirtualizedPages
                pages={pages}
                scrollRef={rightScrollRef}
                slotHeight={SLOT_HEIGHT}
                renderSlot={(pageId, index) => (
                  <div
                    ref={(el) => {
                      if (el) {
                        pageRefs.current[pageId] = el;
                      } else {
                        delete pageRefs.current[pageId];
                      }
                    }}
                  >
                    <RightPageItem
                      documentID={documentID!}
                      pageId={pageId}
                      index={index}
                      zoom={rightZoom.zoom}
                      panCursor={rightPan.cursor}
                      panHandlers={rightPan.panHandlers}
                      activeSearchQuery={activeSearchQuery}
                      isCurrentSearchResult={
                        searchResults.length > 0 &&
                        searchResults[currentResultIndex]?.pageId === pageId
                      }
                      currentOccurrenceIndex={
                        searchResults[currentResultIndex]?.occurrenceIndex
                      }
                      selectedTerm={selectedTerm}
                      onTermSelect={setSelectedTerm}
                      hoveredParentUuid={hoveredObjectUuid}
                      pageRef={() => {}}
                      structuredPageRef={() => {}}
                      refreshKey={pageRefreshKeys[pageId] ?? 0}
                      showDrawings={showDrawings}
                    />
                  </div>
                )}
              />
            )}
          </div>
        </div>
      </div>

      {/* modals */}
      <DeleteDocumentModal
        document={documentData}
        isDeleting={isDeleting}
        onConfirmDelete={confirmDelete}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      />

      <DeletePageModal
        currentPageIndex={currentPageIndex}
        isDeleting={isDeletingPage}
        onConfirmDelete={confirmDeletePage}
        isOpen={isDeletePageModalOpen}
        onClose={() => setIsDeletePageModalOpen(false)}
        isLastPage={pages.length === 1}
      />

      <DocumentUpload
        mode="add"
        documentID={documentID || null}
        isOpen={isAddPagesModalOpen}
        setIsOpen={handleAddPagesModalClose}
      />

      <AddMoleculeModal
        isOpen={moleculeModal !== null}
        setIsOpen={(open) => {
          if (!open) setMoleculeModal(null);
        }}
        pageNumber={(moleculeModal?.pageIndex ?? 0) + 1}
        mode={moleculeModal?.mode ?? "add"}
        initialSmiles={
          moleculeModal?.mode === "edit" ? moleculeModal.initialSmiles : ""
        }
        onSubmit={handleMoleculeModalSubmit}
      />

      <AddReactionModal
        isOpen={reactionModal !== null}
        setIsOpen={(open) => {
          if (!open) setReactionModal(null);
        }}
        pageNumber={(reactionModal?.pageIndex ?? 0) + 1}
        mode={reactionModal?.mode ?? "add"}
        initialRxnSmiles={
          reactionModal?.mode === "edit" ? reactionModal.initialRxnSmiles : ""
        }
        onSubmit={handleReactionModalSubmit}
      />
    </div>
  );
};

export default View;
